import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { patientsAPI, episodesAPI, appointmentsAPI, paymentsAPI, portalAPI, exportAPI, relationshipsAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isClinicalRole } from "@/lib/modules";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  User, Phone, Mail, Calendar, Activity, ArrowLeft, Plus,
  CreditCard, Clock, Link2, Copy, Check, DollarSign, Download, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Fase 5a: roles no clínicos (coach/assistant) no ven episodios ni campos médicos.
  const clinical = isClinicalRole(user?.role);
  const [patient, setPatient] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEpDialog, setShowEpDialog] = useState(false);
  const [epForm, setEpForm] = useState({ title: "", chief_complaint: "" });
  const [saving, setSaving] = useState(false);
  const [portalLink, setPortalLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [statement, setStatement] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, epRes, apRes, payRes] = await Promise.all([
        patientsAPI.get(id),
        // Fase 5a: episodios = expediente clínico; el backend devuelve 403 a roles no clínicos.
        clinical ? episodesAPI.list({ patient_id: id }) : Promise.resolve({ data: { episodes: [] } }),
        appointmentsAPI.list({ patient_id: id, limit: 20 }),
        paymentsAPI.list({ patient_id: id }),
      ]);
      setPatient(pRes.data);
      setEpisodes(epRes.data.episodes || []);
      setAppointments(apRes.data.appointments || []);
      setPayments(payRes.data.payments || []);
      patientsAPI.statement(id).then((r) => setStatement(r.data)).catch(() => {});
    } catch {
      toast.error("Error cargando datos del paciente");
    } finally {
      setLoading(false);
    }
  }, [id, clinical]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateEpisode = async (e) => {
    e.preventDefault();
    if (!epForm.title.trim()) return toast.error("El titulo es requerido");
    setSaving(true);
    try {
      const res = await episodesAPI.create({ ...epForm, patient_id: id });
      toast.success("Episodio creado");
      setShowEpDialog(false);
      setEpForm({ title: "", chief_complaint: "" });
      setTimeout(() => navigate(`/episodios/${res.data.id}`), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear episodio");
    } finally {
      setSaving(false);
    }
  };

  const generatePortalLink = async () => {
    try {
      const res = await portalAPI.generateLink({ patient_id: id });
      const link = `${window.location.origin}/portal?token=${res.data.token}`;
      setPortalLink(link);
    } catch {
      toast.error("Error generando link del portal");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(portalLink);
    setCopied(true);
    toast.success("Link copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const openInvite = () => {
    setInviteEmail(patient?.email || "");
    setShowInviteDialog(true);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error("El correo es requerido");
    setInviting(true);
    try {
      await relationshipsAPI.invite({ subject_id: id, email: inviteEmail.trim() });
      toast.success(`Invitación enviada a ${inviteEmail.trim()}`);
      setShowInviteDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error al enviar la invitación");
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 rounded-xl bg-muted animate-pulse" />
        <div className="h-64 rounded-xl bg-muted animate-pulse" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Paciente no encontrado</p>
        <Button variant="link" onClick={() => navigate("/pacientes")}>Volver a pacientes</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="mt-1"
          onClick={() => navigate("/pacientes")}
          data-testid="back-to-patients"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-bold">
              {patient.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">{patient.name}</h1>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {patient.phone && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="w-3 h-3" />{patient.phone}</span>}
                {patient.email && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="w-3 h-3" />{patient.email}</span>}
                {patient.date_of_birth && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="w-3 h-3" />{patient.date_of_birth}</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" size="sm" className="rounded-full" onClick={openInvite} data-testid="invite-enduser-btn">
            <UserPlus className="w-4 h-4 mr-1.5" />Invitar
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={generatePortalLink} data-testid="generate-portal-link">
            <Link2 className="w-4 h-4 mr-1.5" />Portal
          </Button>
          {clinical && (
            <Button size="sm" className="rounded-full" onClick={() => { setEpForm({ title: "", chief_complaint: "" }); setShowEpDialog(true); }} data-testid="new-episode-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nuevo episodio
            </Button>
          )}
        </div>
      </div>

      {/* Portal Link */}
      {portalLink && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-3 flex items-center gap-3">
            <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
            <code className="text-xs flex-1 truncate text-foreground">{portalLink}</code>
            <Button size="sm" variant="ghost" onClick={copyLink} data-testid="copy-portal-link">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue={clinical ? "episodes" : "appointments"} className="w-full">
        <TabsList className="w-full sm:w-auto">
          {clinical && (
            <TabsTrigger value="episodes" data-testid="tab-episodes">
              <Activity className="w-3.5 h-3.5 mr-1.5" />Episodios ({episodes.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="appointments" data-testid="tab-appointments">
            <Clock className="w-3.5 h-3.5 mr-1.5" />Citas ({appointments.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" />Pagos ({payments.length})
          </TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">
            <User className="w-3.5 h-3.5 mr-1.5" />Informacion
          </TabsTrigger>
        </TabsList>

        <TabsContent value="episodes" className="mt-4">
          {episodes.length > 0 ? (
            <div className="space-y-2">
              {episodes.map((ep) => (
                <Card
                  key={ep.id}
                  className="cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => navigate(`/episodios/${ep.id}`)}
                  data-testid={`episode-card-${ep.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-2 h-10 rounded-full ${ep.status === "active" ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{ep.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ep.chief_complaint || "Sin queja principal"} &middot; {ep.notes_count || 0} nota{ep.notes_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <Badge variant={ep.status === "active" ? "default" : "secondary"} className="text-[10px]">
                      {ep.status === "active" ? "Activo" : "Completado"}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin episodios registrados</p>
              <Button variant="link" size="sm" className="mt-1" onClick={() => setShowEpDialog(true)}>Crear primer episodio</Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="appointments" className="mt-4">
          {appointments.length > 0 ? (
            <div className="space-y-2">
              {appointments.map((a) => (
                <Card key={a.id} data-testid={`appt-card-${a.id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="text-center w-14 flex-shrink-0">
                      <p className="text-xs text-muted-foreground">{a.date}</p>
                      <p className="text-sm font-semibold">{a.start_time}</p>
                    </div>
                    <div className="w-px h-8 bg-border" />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{a.notes || "Cita programada"}</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {a.status === "scheduled" ? "Programada" : a.status === "confirmed" ? "Confirmada" : a.status === "completed" ? "Completada" : a.status}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin citas registradas</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          {/* Statement Summary */}
          {statement && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <Card className="stat-card">
                <CardContent className="p-4 flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total pagado</p>
                    <p className="text-xl font-bold mt-0.5">${statement.total_paid?.toLocaleString("es-MX")}</p>
                  </div>
                  <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-4 flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Citas totales</p>
                    <p className="text-xl font-bold mt-0.5">{statement.appointments_total || 0}</p>
                  </div>
                  <Calendar className="w-4 h-4 text-primary" />
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-4 flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Citas completadas</p>
                    <p className="text-xl font-bold mt-0.5">{statement.appointments_completed || 0}</p>
                  </div>
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </CardContent>
              </Card>
            </div>
          )}
          {payments.length > 0 ? (
            <div className="space-y-2">
              {payments.map((p) => (
                <Card key={p.id} data-testid={`payment-card-${p.id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <DollarIcon className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">${p.amount?.toLocaleString("es-MX")}</p>
                      <p className="text-xs text-muted-foreground">{p.method} &middot; {p.created_at?.split("T")[0]}</p>
                    </div>
                    {p.notes && <p className="text-xs text-muted-foreground max-w-[150px] truncate">{p.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin pagos registrados</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <Card>
            <CardContent className="p-5 space-y-4">
              <InfoRow label="Nombre" value={patient.name} />
              <InfoRow label="Email" value={patient.email} />
              <InfoRow label="Telefono" value={patient.phone} />
              <InfoRow label="Fecha de nacimiento" value={patient.date_of_birth} />
              <InfoRow label="Genero" value={patient.gender} />
              <InfoRow label="Ocupacion" value={patient.occupation} />
              <InfoRow label="Contacto de emergencia" value={patient.emergency_contact_name ? `${patient.emergency_contact_name} - ${patient.emergency_contact_phone}` : ""} />
              {clinical && <InfoRow label="Antecedentes medicos" value={patient.medical_history} />}
              {clinical && <InfoRow label="Alergias" value={patient.allergies} />}
              <InfoRow label="Notas" value={patient.notes} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Episode Dialog */}
      <Dialog open={showEpDialog} onOpenChange={setShowEpDialog}>
        <DialogContent className="sm:max-w-md" data-testid="new-episode-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo episodio de atencion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEpisode} className="space-y-4">
            <div>
              <Label>Titulo del episodio *</Label>
              <Input
                value={epForm.title}
                onChange={(e) => setEpForm({ ...epForm, title: e.target.value })}
                placeholder="Ej: Lumbalgia mecanica"
                required
                data-testid="episode-title-input"
              />
            </div>
            <div>
              <Label>Motivo de consulta</Label>
              <Textarea
                value={epForm.chief_complaint}
                onChange={(e) => setEpForm({ ...epForm, chief_complaint: e.target.value })}
                placeholder="Descripcion de la queja principal..."
                rows={3}
                data-testid="episode-complaint-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowEpDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="episode-save-btn">
                {saving ? "Creando..." : "Crear episodio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invitar al usuario final a su cuenta/app (Fase 4) */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md" data-testid="invite-enduser-dialog">
          <DialogHeader>
            <DialogTitle>Invitar a su cuenta</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le enviaremos a <span className="font-medium text-foreground">{patient.name}</span> un enlace
              para activar su cuenta y ver sus planes desde la app. Su historial lo acompaña aunque cambie de profesional.
            </p>
            <div>
              <Label>Correo *</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                data-testid="invite-email-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowInviteDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={inviting} data-testid="invite-send-btn">
                {inviting ? "Enviando..." : "Enviar invitación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1">
      <span className="text-xs font-medium text-muted-foreground w-40 flex-shrink-0">{label}</span>
      <span className="text-sm text-foreground">{value || "-"}</span>
    </div>
  );
}

function DollarIcon({ className }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <CreditCard className="w-4 h-4" />
    </div>
  );
}
